import React, { useEffect, useState, useRef, useContext } from "react";
import Logo from "../../images/MY-Anlook.png";
import { Link } from "react-router-dom";
import useHeatMap from "../../services/LandingPage/useHeatMap";
import { GlobalContext } from "../../Context/GlobalContext";

const Navbar = () => {
  const { login } = useContext(GlobalContext);

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false);

  const menuWrapperRef = useRef(null);
  const menuRef = useRef(null);
  const serviceDropdownRef = useRef(null);

  const [sessionID, setSessionId] = useState("");
  const [campaignIdHook, setCampaignIdHook] = useState("");

  const { trackClick } = useHeatMap();

  // ================= Scroll Effect =================
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);

    window.addEventListener("scroll", onScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // ================= Outside Click =================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen &&
        menuWrapperRef.current &&
        !menuWrapperRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
        setMobileServiceOpen(false);
      }

      if (
        serviceDropdownRef.current &&
        !serviceDropdownRef.current.contains(event.target)
      ) {
        setServiceDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // ================= Campaign ID =================
  useEffect(() => {
    let params = new URLSearchParams(window.location.search);
    let campaignid = params.get("campaignid");

    if (!campaignid) {
      params = new URLSearchParams(window.location.hash.substring(1));
      campaignid = params.get("campaignid");
    }

    if (campaignid) {
      sessionStorage.setItem("campaignId", campaignid);
      setCampaignIdHook(campaignid);
    }
  }, []);

  // ================= Body Scroll Lock =================
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen]);

  // ================= Heatmap Click =================
  const handleTrackClick = async (buttonName) => {
    await trackClick({
      campaignid: campaignIdHook,
      sessionid: sessionID,
      element: "Button",
      button_name: buttonName,
      pageurl: window.location.href,
      pagename: "Navbar",
      product: "Anlook",
    });
  };

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setMobileServiceOpen(false);
  };

  return (
    <>
      <div
        className={`sticky top-0 z-[500] h-[75px] bg-white transition-shadow duration-300 ${
          scrolled ? "shadow-md" : ""
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-20">
          <div
            ref={menuWrapperRef}
            className="flex h-[75px] items-center justify-between"
          >
            {/* LOGO */}
            <Link to="/">
              <img src={Logo} alt="Anlook Logo" className="w-[160px]" />
            </Link>

            {/* DESKTOP LINKS */}
            <div className="hidden items-center gap-8 lg:flex">
              <Link to="/" className="nav-link">
                Home
              </Link>

              <Link to="/about-us" className="nav-link">
                About
              </Link>

              <Link to="/blogs" className="nav-link">
                Blogs
              </Link>

              <Link to="/contact-us" className="nav-link">
                Contact us
              </Link>

              {/* SERVICES DROPDOWN */}
              <div
                ref={serviceDropdownRef}
                className="relative"
                onMouseEnter={() => setServiceDropdownOpen(true)}
                onMouseLeave={() => setServiceDropdownOpen(false)}
              >
                <button
                  type="button"
                  onClick={() =>
                    setServiceDropdownOpen((previous) => !previous)
                  }
                  className="nav-link flex items-center gap-1 bg-transparent"
                >
                  Services
                  <svg
                    className={`h-4 w-4 transition-transform duration-200 ${
                      serviceDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {serviceDropdownOpen && (
                  <div className="absolute right-0 top-full z-[2500] w-[220px] pt-3">
                    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white py-2 shadow-xl">
                      <Link
                        to="/whatsapp-order"
                        onClick={() => {
                          handleTrackClick("WhatsApp Order");
                          setServiceDropdownOpen(false);
                        }}
                        className="block px-5 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-green-50 hover:text-[#01C37D]"
                      >
                        WhatsApp Order
                      </Link>

                      <Link
                        to="/pos-order"
                        onClick={() => {
                          handleTrackClick("POS Order");
                          setServiceDropdownOpen(false);
                        }}
                        className="block px-5 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-green-50 hover:text-[#01C37D]"
                      >
                        POS Order
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* DESKTOP BUTTONS */}
            <div className="hidden gap-2 lg:flex">
              <Link
                to="/get-started"
                onClick={() => handleTrackClick("Get Started")}
                className="rounded-lg bg-[#01C37D] px-[22px] py-2 font-semibold text-white transition hover:opacity-90"
              >
                Start Free Trial
              </Link>

              <button
                type="button"
                onClick={() => {
                  handleTrackClick("Login");
                  login();
                }}
                className="rounded-md bg-green-600 px-4 py-2 text-white transition hover:opacity-90"
              >
                Login
              </button>
            </div>

            {/* MOBILE MENU ICON */}
            <button
              type="button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => {
                setMenuOpen((previous) => !previous);

                if (menuOpen) {
                  setMobileServiceOpen(false);
                }
              }}
              className="block cursor-pointer text-[28px] lg:hidden"
            >
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div
          ref={menuRef}
          className="fixed left-0 top-[75px] z-[2000] max-h-[calc(100vh-75px)] w-full overflow-y-auto bg-white p-5 text-center shadow-lg lg:hidden"
        >
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="block border-b border-gray-100 py-3"
          >
            Home
          </Link>

          <Link
            to="/about-us"
            onClick={closeMobileMenu}
            className="block border-b border-gray-100 py-3"
          >
            About
          </Link>

          <Link
            to="/blogs"
            onClick={closeMobileMenu}
            className="block border-b border-gray-100 py-3"
          >
            Blogs
          </Link>

          <Link
            to="/contact-us"
            onClick={closeMobileMenu}
            className="block border-b border-gray-100 py-3"
          >
            Contact us
          </Link>

          {/* MOBILE SERVICES DROPDOWN */}
          <div className="border-b border-gray-100">
            <button
              type="button"
              onClick={() => setMobileServiceOpen((previous) => !previous)}
              className="flex w-full items-center justify-center gap-2 py-3"
            >
              Services
              <svg
                className={`h-4 w-4 transition-transform duration-200 ${
                  mobileServiceOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {mobileServiceOpen && (
              <div className="mb-3 overflow-hidden rounded-lg bg-gray-50">
                <Link
                  to="/whatsapp-order"
                  onClick={() => {
                    handleTrackClick("WhatsApp Order");
                    closeMobileMenu();
                  }}
                  className="block border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-700"
                >
                  WhatsApp Order
                </Link>

                <Link
                  to="/pos-order"
                  onClick={() => {
                    handleTrackClick("POS Order");
                    closeMobileMenu();
                  }}
                  className="block px-4 py-3 text-sm font-medium text-gray-700"
                >
                  POS Order
                </Link>
              </div>
            )}
          </div>

          <div className="mt-5 flex flex-col gap-4">
            <button
              type="button"
              onClick={() => {
                handleTrackClick("Login");
                closeMobileMenu();
                login();
              }}
              className="rounded-md bg-green-600 px-4 py-3 text-white"
            >
              Login
            </button>

            <Link
              to="/get-started"
              onClick={() => {
                handleTrackClick("Get Started");
                closeMobileMenu();
              }}
              className="block rounded-lg bg-[#01C37D] py-3 font-semibold text-white"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
